import { useQuery } from '@tanstack/react-query';
import cn from 'classnames';
import { AgentResponseSegment } from '../../utils/agent-client';
import { fetchPendingAgentOrders, QUERY_KEY } from '../AgentOrderApproval/AgentOrderApproval';
import { LiveBuyingPolicy } from './governance-live';
import {
  buildOrchestrationWeb,
  LIVE_INVENTORY,
  OrchestrationWeb,
  ROADMAP_SUMMARY,
  WebNode
} from './orchestration-web-model';

// The orchestration WEB — the demo's centerpiece. Instead of a linear checklist,
// it draws the actual system: one governed runtime under a shared agent registry +
// audit log, where the planner and buying stages run as require-scoped, audited
// governed steps with human gates on every hand-off — each agent wired to the real
// tools, Newton blueprints, skill, and guardrails it uses. The 2027-vision agents
// are drawn dimmed as roadmap (Newton-provided ones attributed to Newton) so the
// picture is honest about what runs today vs what's next. Live agents' status is
// derived from the agent's real tool-call stream + order state (same source as the
// linear trace).

interface OrchestrationWebPanelProps {
  company: string;
  segments: AgentResponseSegment[];
  newtonReplayed: boolean;
  // The resolved durable buying policy from the live governance.policy event —
  // threaded through so the buying guardrail chips show the SAME live-resolved
  // numbers the GovernancePanel shows, and never a different number for one control.
  livePolicy?: LiveBuyingPolicy;
}

const AGENT_W = 156;
const AGENT_H = 46;
const ORCH_W = 236;
const ORCH_H = 50;
const LEAF_W = 180;
const LEAF_H = 20;
// Half-width of a gate diamond along the flow line, used to butt the flow arrows up
// against (but not through) each diamond so the sequence reads as arrow → gate → arrow.
const GATE_HALF = 12;

export const OrchestrationWebPanel = ({
  company,
  segments,
  newtonReplayed,
  livePolicy
}: OrchestrationWebPanelProps) => {
  const { data: orders } = useQuery({
    queryKey: [QUERY_KEY, company],
    queryFn: () => fetchPendingAgentOrders(company),
    enabled: Boolean(company)
  });

  const web = buildOrchestrationWeb({ segments, newtonReplayed, orders, livePolicy });

  return (
    <div className='elevate-web'>
      <div className='elevate-web-head'>
        <h2 className='elevate-web-title'>Orchestration — the governed agent web</h2>
        <p className='elevate-web-sub'>
          {LIVE_INVENTORY.agents} agents · {LIVE_INVENTORY.gates} gates · {LIVE_INVENTORY.tools}{' '}
          tools · {LIVE_INVENTORY.guardrails} guardrails · faded = not yet live
        </p>
      </div>
      <div className='elevate-web-canvas'>
        <WebSvg web={web} />
      </div>
      <WebLegend />
      <RoadmapSummary />
    </div>
  );
};

// The 2027 roadmap collapsed to one compact line (was a multi-row grid of dimmed
// nodes). Keeps the info but subordinate to the flow; names the Newton-provided
// agents so provenance stays honest.
const RoadmapSummary = () => (
  <p className='elevate-web-roadmap'>
    <span className='elevate-web-roadmap-lead'>+{ROADMAP_SUMMARY.total} on the 2027 roadmap</span>{' '}
    (pre-flight → execution → in-flight → post-flight). Bliss Point Audience &amp; Planner–provided:{' '}
    {ROADMAP_SUMMARY.newtonNames.join(' · ')}.
  </p>
);

// The full governed flow, agents and gates interleaved in run order. Rendering an
// arrowed segment between each consecutive pair makes every hand-off pass THROUGH
// its gate diamond(s) and shows G1 → G2 → G2.5 → G3 as one legible sequence.
const FLOW_SEQUENCE = [
  'newton-preflight',
  'gate-1',
  'intake',
  'streaming-planner',
  'gate-2',
  'gate-2_5',
  'buying',
  'gate-3'
];

// The point on a node's right / left edge where a flow arrow attaches. Gates butt up
// against the diamond's horizontal tip; agents against the box edge.
const flowEdgeRight = (node: WebNode): number =>
  node.x + (node.kind === 'gate' ? GATE_HALF : AGENT_W / 2);
const flowEdgeLeft = (node: WebNode): number =>
  node.x - (node.kind === 'gate' ? GATE_HALF : AGENT_W / 2);

interface WebSvgProps {
  web: OrchestrationWeb;
}

const WebSvg = ({ web }: WebSvgProps) => {
  const nodeById = new Map(web.nodes.map(n => [n.id, n]));

  // The agent row runs at a single y; use it to place the runtime lane behind it.
  const agentY = web.nodes.find(n => n.kind === 'agent')?.y ?? 176;

  // Spine: connect each agent to the bottom of its leaf cluster.
  const spines = ['newton-preflight', 'intake', 'streaming-planner', 'buying']
    .map(parent => {
      const agent = nodeById.get(parent);
      const leaves = web.nodes.filter(n => n.parent === parent);
      if (!agent || leaves.length === 0) return undefined;
      const maxY = Math.max(...leaves.map(l => l.y));
      return { x: agent.x, y1: agent.y + AGENT_H / 2, y2: maxY };
    })
    .filter((s): s is { x: number; y1: number; y2: number } => Boolean(s));

  return (
    <svg
      className='elevate-web-svg'
      viewBox={`0 0 ${web.width} ${web.height}`}
      role='img'
      aria-label='Orchestration web of governed agents, tools, gates, and guardrails'
      preserveAspectRatio='xMidYMin meet'
    >
      <defs>
        <marker
          id='elevate-web-arrow'
          viewBox='0 0 10 10'
          refX='9'
          refY='5'
          markerWidth='7'
          markerHeight='7'
          orient='auto-start-reverse'
        >
          <path d='M0,0 L10,5 L0,10 z' className='elevate-web-arrowhead' />
        </marker>
      </defs>

      {/* runtime lane — one shared band behind the agent row, replacing the dashed
          "coordinate" fan that used to read as a mastermind dispatching four agents */}
      <rect
        className='elevate-web-lane'
        x={40}
        y={agentY - 40}
        width={web.width - 80}
        height={80}
        rx={14}
      />

      {/* leaf spines */}
      {spines.map(s => (
        <line
          key={`spine-${s.x}`}
          className='elevate-web-spine'
          x1={s.x}
          y1={s.y1}
          x2={s.x}
          y2={s.y2}
        />
      ))}

      {/* flow — one left→right sequence drawn as arrowed segments between every
          consecutive stop (agent AND gate), so each arrow visibly enters the next
          gate diamond and the order reads unambiguously:
            Newton → G1 → Intake → Planner → G2 → G2.5 → Buying → G3
          The runtime's relationship to the agents is carried by the lane above, so
          the old dashed "coordinate" fan is not rendered. */}
      {FLOW_SEQUENCE.slice(0, -1).map((fromId, i) => {
        const from = nodeById.get(fromId);
        const to = nodeById.get(FLOW_SEQUENCE[i + 1]);
        if (!from || !to) return undefined;
        return (
          <line
            key={`flow-${fromId}`}
            className='elevate-web-edge is-handoff'
            x1={flowEdgeRight(from)}
            y1={from.y}
            x2={flowEdgeLeft(to)}
            y2={to.y}
            markerEnd='url(#elevate-web-arrow)'
          />
        );
      })}

      {/* nodes */}
      {web.nodes.map(node => (
        <WebNodeShape key={node.id} node={node} />
      ))}
    </svg>
  );
};

interface WebNodeShapeProps {
  node: WebNode;
}

const WebNodeShape = ({ node }: WebNodeShapeProps) => {
  const statusClass = node.status ? `is-${node.status}` : undefined;

  if (node.kind === 'orchestrator') {
    return (
      <g className='elevate-web-node is-orchestrator'>
        <rect
          x={node.x - ORCH_W / 2}
          y={node.y - ORCH_H / 2}
          width={ORCH_W}
          height={ORCH_H}
          rx={10}
        />
        <text x={node.x} y={node.y - 3} className='elevate-web-node-label'>
          {node.label}
        </text>
        <text x={node.x} y={node.y + 12} className='elevate-web-node-tag'>
          shared registry · scopes · audit trail
        </text>
      </g>
    );
  }

  if (node.kind === 'agent') {
    return (
      <g className={cn('elevate-web-node is-agent', statusClass)}>
        <rect
          x={node.x - AGENT_W / 2}
          y={node.y - AGENT_H / 2}
          width={AGENT_W}
          height={AGENT_H}
          rx={8}
        />
        <text x={node.x} y={node.y - 4} className='elevate-web-node-label'>
          {node.label}
        </text>
        <text x={node.x} y={node.y + 11} className='elevate-web-node-scope'>
          {node.scope}
        </text>
      </g>
    );
  }

  if (node.kind === 'gate') {
    // Diamond sits ON the flow line at node.y; its 2-line label (split on ' · ')
    // renders up in the shared band at node.labelY, centered over the diamond with a
    // white halo — so no gate label ever touches an agent box or a chip, and all four
    // gate labels align on one consistent horizontal band.
    const labelLines = node.label.split(' · ');
    const firstLineY = node.labelY ?? node.y - 30;
    return (
      <g className='elevate-web-node is-gate'>
        {/* faint tie from the label band down to the diamond, so a reader can tell
            which diamond a banded label belongs to */}
        <line
          className='elevate-web-gate-tie'
          x1={node.x}
          y1={firstLineY + 6}
          x2={node.x}
          y2={node.y - 9}
        />
        <rect
          className='elevate-web-gate-diamond'
          x={node.x - 9}
          y={node.y - 9}
          width={18}
          height={18}
          transform={`rotate(45 ${node.x} ${node.y})`}
        />
        <text x={node.x} y={firstLineY} className='elevate-web-gate-label'>
          {labelLines.map((line, index) => (
            <tspan key={line} x={node.x} dy={index === 0 ? 0 : 12}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  }

  // tool / blueprint / skill / guardrail leaves
  return (
    <g
      className={cn('elevate-web-node is-leaf', `is-${node.kind}`, {
        'is-not-live': !node.live,
        // A guardrail chip whose displayed value is a config default, not a live
        // reading — rendered muted so a hardcoded number can never be mistaken for a
        // live one (matches the GovernancePanel's not-live treatment two feet below).
        'is-default-value': node.valueIsLive === false
      })}
    >
      <rect x={node.x - LEAF_W / 2} y={node.y - LEAF_H / 2} width={LEAF_W} height={LEAF_H} rx={5} />
      <text x={node.x} y={node.y + 4} className='elevate-web-node-leaf-label'>
        {node.label}
      </text>
    </g>
  );
};

const LEGEND: { cls: string; label: string }[] = [
  { cls: 'is-done', label: 'agent done' },
  { cls: 'is-active', label: 'agent active' },
  { cls: 'is-pending', label: 'agent pending' },
  { cls: 'is-gate', label: 'human gate' },
  { cls: 'is-guardrail', label: 'code guardrail' },
  { cls: 'is-roadmap', label: 'wired but not yet live' }
];

const WebLegend = () => (
  <ul className='elevate-web-legend'>
    {LEGEND.map(item => (
      <li key={item.cls} className='elevate-web-legend-item'>
        <span className={cn('elevate-web-legend-swatch', item.cls)} />
        {item.label}
      </li>
    ))}
  </ul>
);
